import WalletButton from 'features/cardManagement/components/WalletButton/WalletButton';
import { WalletButtonProps } from 'features/cardManagement/components/WalletButton/WalletButtonShared';
import React from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button, { ButtonProps } from 'shared/components/vxl/Button/Button';
import Illustration, {
  IllustrationKeyType,
} from 'shared/components/vxl/Illustration/Illustration';
import { isAndroid, isWeb } from 'shared/constants/platform';
import theme from 'shared/styles/theme';

import { RenderText } from './base/RenderText';

export interface ActionScreenProps {
  bodyContent?: React.ReactNode;
  titleText?: string;
  titleTextStyle?: 'large' | 'extra-large';
  /** Visual key should be an illustration key */
  visual: IllustrationKeyType;
  visualAccessibilityLabel?: string;
  primaryButton?: Pick<
    ButtonProps,
    | 'label'
    | 'isDisabled'
    | 'onPress'
    | 'isLoading'
    | 'analyticsId'
    | 'customAttributes'
  >;
  secondaryButton?: Pick<
    ButtonProps,
    | 'label'
    | 'isDisabled'
    | 'onPress'
    | 'isLoading'
    | 'analyticsId'
    | 'customAttributes'
  >;
  addWalletButton?: WalletButtonProps;
  includeDefaultPadding?: boolean;
  topPadding?: number;
}

const ActionScreen = ({
  bodyContent,
  primaryButton,
  secondaryButton,
  addWalletButton,
  titleText,
  titleTextStyle,
  visual,
  visualAccessibilityLabel,
  includeDefaultPadding = true,
  topPadding = 0,
}: ActionScreenProps) => {
  const { bottom } = useSafeAreaInsets();

  return (
    <View style={styles.container}>
      <View style={{ paddingTop: topPadding }} />
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={[
          styles.scrollContentContainer,
          includeDefaultPadding ? styles.defaultPadding : styles.noPadding,
        ]}
      >
        {visual ? (
          <View
            style={styles.visualContainer}
            accessibilityLabel={visualAccessibilityLabel}
          >
            <Illustration illustration={visual as IllustrationKeyType} />
          </View>
        ) : null}
        {titleText ? (
          <View
            style={
              !includeDefaultPadding
                ? styles.titleText
                : styles.titleTextPaddingVertical
            }
          >
            <RenderText titleText={titleText} titleTextStyle={titleTextStyle} />
          </View>
        ) : null}
        {bodyContent ? <View>{bodyContent}</View> : null}
      </ScrollView>
      {primaryButton || secondaryButton || addWalletButton ? (
        <View
          style={[
            styles.footerContainer,
            {
              paddingBottom: isAndroid
                ? bottom + theme.spacing.m
                : bottom + theme.spacing.l,
            },
          ]}
        >
          {primaryButton ? (
            <Button
              label={primaryButton.label}
              onPress={primaryButton.onPress}
              isDisabled={primaryButton.isDisabled}
              isLoading={primaryButton.isLoading}
              analyticsId={primaryButton.analyticsId}
              customAttributes={primaryButton.customAttributes}
              size="LARGE"
              type="PRIMARY"
              width="FULL"
            />
          ) : null}
          {addWalletButton && !isWeb ? (
            <WalletButton {...addWalletButton} style={styles.walletButton} />
          ) : null}
          {secondaryButton && (primaryButton || addWalletButton) ? (
            <Button
              label={secondaryButton.label}
              onPress={secondaryButton.onPress}
              isDisabled={secondaryButton.isDisabled}
              isLoading={secondaryButton.isLoading}
              analyticsId={secondaryButton.analyticsId}
              customAttributes={secondaryButton.customAttributes}
              size="LARGE"
              type="SECONDARY"
              width="FULL"
            />
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  defaultPadding: {
    padding: theme.spacing.m,
  },
  footerContainer: {
    borderTopColor: theme.color.border.primarySolid,
    borderTopWidth: 1,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  noPadding: {
    padding: 0,
  },
  scrollContainer: {
    flex: 1,
    paddingTop: theme.spacing.m,
  },
  scrollContentContainer: {
    flexGrow: 1,
    gap: theme.spacing.m,
  },
  titleText: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  titleTextPaddingVertical: { paddingTop: theme.spacing.m },
  visualContainer: {
    alignItems: 'center',
    alignSelf: 'center',
    height: 168,
    width: 168,
  },
  ...Platform.select({
    android: {
      walletButton: {
        height: 50,
        width: '100%',
      },
    },
    ios: {
      walletButton: {
        height: 40,
        width: '100%',
      },
    },
  }),
});

export default ActionScreen;
